// ─────────────────────────────────────────────────────────────────
// FindRE — staff request authentication
//
// The dashboard used to talk to Supabase using the anon key, which is
// shipped inside the public JavaScript bundle. The database therefore had
// no idea WHO was asking — it only checked that the caller held a key that
// every visitor already has. The login screen was not a security boundary.
//
// Every staff request now carries the signed-in user's own access token, so
// the database can tell staff from strangers and the `authenticated` policy
// does the work.
//
// The `apikey` header still carries the anon key — Supabase requires it to
// route the request to the right project. It is the Authorization header
// that decides access.
// ─────────────────────────────────────────────────────────────────

import { supabase } from './supabaseClient';

/**
 * Headers for a staff (logged-in) request to Supabase REST or Storage.
 *
 * Throws when there is no session. That is deliberate: silently falling back
 * to the shared anon key is exactly the behaviour we are removing, and a loud
 * failure is far better than a quiet one that still works for everybody.
 *
 * @param {{url:string, anonKey:string}} cfg
 * @param {Object} [extra] additional headers (Content-Type, Prefer, ...)
 */
export async function staffAuthHeaders(cfg, extra = {}) {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw new Error(`[auth] could not read the session: ${error.message}`);
  }
  const token = data?.session?.access_token;
  if (!token) {
    throw new Error('[auth] not signed in — refusing to fall back to the shared key');
  }
  return {
    apikey: cfg.anonKey,
    Authorization: `Bearer ${token}`,
    ...extra,
  };
}
